import { useState, useEffect } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@shadcn/components/ui/table";
import { Button } from "@shadcn/components/ui/button";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@shadcn/components/ui/card";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose
} from "@shadcn/components/ui/dialog";
import {
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@shadcn/components/ui/dropdown-menu";
import { Input } from "@shadcn/components/ui/input";
import { Label } from "@shadcn/components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@shadcn/components/ui/select";
import { MoreHorizontal, Plus, Edit, Trash, Eye, Loader2 } from "lucide-react";
import { carrierPerformanceService } from './carrier-performance.service';
import { toast, ToastContainer } from 'react-toastify';


function CarrierPerformanceList() {
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countries, setCountries] = useState({});
  const [carrierNames, setCarrierNames] = useState([]);
  const [formData, setFormData] = useState({
    carrierName: '',
    country: '',
    state: '',
    standardDeliveryDays: {
      min: 0,
      max: 0,
    },
    expressDeliveryDays: {
      min: 0,
      max: 0,
    },
  });
  const [availableStates, setAvailableStates] = useState([]);

  useEffect(() => {
    fetchCarriers();
    fetchCountriesAndStates();
    fetchCarrierNames();
  }, []);

  useEffect(() => {
    // Update available states when country changes
    if (formData.country && countries[formData.country]) {
      setAvailableStates(countries[formData.country]);
    } else {
      setAvailableStates([]);
    }
    
    // Reset state when country changes
    if (formData.country) {
      setFormData(prev => ({
        ...prev,
        state: ''
      }));
    }
  }, [formData.country, countries]);

  const fetchCarriers = async () => {
    try {
      setLoading(true);
      const data = await carrierPerformanceService.getAll();
      setCarriers(data);
    } catch (error) {
      toast.error("Failed to fetch carrier performance data");
    } finally {
      setLoading(false);
    }
  };

  const fetchCountriesAndStates = async () => {
    try {
      const data = await carrierPerformanceService.getCountryAndState();
      console.log("test3" ,data);
      setCountries(data);
    } catch (error) {
      toast.error("Failed to fetch countries and states");
    }
  };

  const fetchCarrierNames = async () => {
    try {
      const data = await carrierPerformanceService.getCarrierName();
      setCarrierNames(data);
    } catch (error) {
      toast.error("Failed to fetch carrier names");
    }
  };

  const validateForm = () => {
    // Check if required fields are filled
    if (!formData.carrierName.trim()) {
      toast.error("Carrier name is required");
      return false;
    }
    
    if (!formData.country.trim()) {
      toast.error("Country is required");
      return false;
    }
    
    if (!formData.state.trim()) {
      toast.error("State is required");
      return false;
    }
    
    // Validate standard delivery days
    if (formData.standardDeliveryDays.min < 0) {
      toast.error("Standard delivery minimum days cannot be negative");
      return false;
    }
    
    if (formData.standardDeliveryDays.max < formData.standardDeliveryDays.min) {
      toast.error("Standard delivery maximum days must be greater than or equal to minimum days");
      return false;
    }
    
    // Validate express delivery days
    if (formData.expressDeliveryDays.min < 0) {
      toast.error("Express delivery minimum days cannot be negative");
      return false;
    }
    
    if (formData.expressDeliveryDays.max < formData.expressDeliveryDays.min) {
      toast.error("Express delivery maximum days must be greater than or equal to minimum days");
      return false;
    }
    
    // Validate that express is faster than standard
    if (formData.expressDeliveryDays.min > formData.standardDeliveryDays.min) {
      toast.error("Express delivery minimum days should be less than or equal to standard delivery minimum days");
      return false;
    }
    
    if (formData.expressDeliveryDays.max > formData.standardDeliveryDays.max) {
      toast.error("Express delivery maximum days should be less than or equal to standard delivery maximum days");
      return false;
    }
    
    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: parseInt(value, 10) || 0
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCreate = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      
      
      await carrierPerformanceService.create(formData);
      toast.success("Carrier performance created successfully");
      setIsCreateDialogOpen(false);
      resetForm();
      fetchCarriers();
    } catch (error) {
      toast.error("Failed to create carrier performance");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await carrierPerformanceService.update(selectedCarrier._id, formData);
      toast.success("Carrier performance updated successfully");
      setIsEditDialogOpen(false);
      fetchCarriers();
    } catch (error) {
      toast.error("Failed to update carrier performance");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      await carrierPerformanceService.delete(selectedCarrier._id);
      toast.success("Carrier performance deleted successfully");
      setIsDeleteDialogOpen(false);
      fetchCarriers();
    } catch (error) {
      toast.error("Failed to delete carrier performance");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setIsCreateDialogOpen(true);
  };

  const openEditDialog = (carrier) => {
    setSelectedCarrier(carrier);
    
    // Find the country for this state
    let carrierCountry = '';
    for (const [country, states] of Object.entries(countries)) {
      if (states.includes(carrier.state)) {
        carrierCountry = country;
        break;
      }
    }
    
    setFormData({
      carrierName: carrier.carrierName,
      country: carrierCountry,
      state: carrier.state,
      standardDeliveryDays: {
        min: carrier.standardDeliveryDays.min,
        max: carrier.standardDeliveryDays.max,
      },
      expressDeliveryDays: {
        min: carrier.expressDeliveryDays.min,
        max: carrier.expressDeliveryDays.max,
      },
    });
    setIsEditDialogOpen(true);
  };

  const openViewDialog = (carrier) => {
    setSelectedCarrier(carrier);
    setIsViewDialogOpen(true);
  };

  const openDeleteDialog = (carrier) => {
    setSelectedCarrier(carrier);
    setIsDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      carrierName: '',
      country: '',
      state: '',
      standardDeliveryDays: {
        min: 0,
        max: 0,
      },
      expressDeliveryDays: {
        min: 0,
        max: 0,
      },
    });
  };

  const renderForm = (isEdit = false) => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="carrierName">Carrier Name</Label>
          <Select
            value={formData.carrierName}
            onValueChange={(value) => handleSelectChange('carrierName', value)}
          >
            <SelectTrigger id="carrierName">
              <SelectValue placeholder="Select carrier name" />
            </SelectTrigger>
            <SelectContent>
              {carrierNames && carrierNames?.filter(a=> a !== "")?.map((name) => (
                <SelectItem key={name} value={name}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {console.log("test" ,countries)}
        <div className="space-y-2">
          <Label htmlFor="country">Country</Label>
          <Select
            value={formData.country}
            onValueChange={(value) => handleSelectChange('country', value)}
          >
            <SelectTrigger id="country">
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              { countries && Object.keys(countries)?.map((country) => (
                <SelectItem key={country} value={country}>
                  {country}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="state">State</Label>
          <Select
            value={formData.state}
            onValueChange={(value) => handleSelectChange('state', value)}
            disabled={!formData.country}
          >
            <SelectTrigger id="state">
              <SelectValue placeholder={formData.country ? "Select state" : "Select country first"} />
            </SelectTrigger>
            <SelectContent>
              {availableStates?.map((state) => (
                <SelectItem key={state} value={state}>
                  {state}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Standard Delivery Days</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="standardMin" className="text-xs">Min</Label>
              <Input
                id="standardMin"
                name="standardDeliveryDays.min"
                type="number"
                min="0"
                value={formData.standardDeliveryDays.min}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <Label htmlFor="standardMax" className="text-xs">Max</Label>
              <Input
                id="standardMax"
                name="standardDeliveryDays.max"
                type="number"
                min={formData.standardDeliveryDays.min}
                value={formData.standardDeliveryDays.max}
                onChange={handleChange}
                required
              />
            </div>
          </div>
        </div>
        
        <div className="space-y-2">
          <Label>Express Delivery Days</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="expressMin" className="text-xs">Min</Label>
              <Input
                id="expressMin"
                name="expressDeliveryDays.min"
                type="number"
                min="0"
                max={formData.standardDeliveryDays.min}
                value={formData.expressDeliveryDays.min}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <Label htmlFor="expressMax" className="text-xs">Max</Label>
              <Input
                id="expressMax"
                name="expressDeliveryDays.max"
                type="number"
                min={formData.expressDeliveryDays.min}
                max={formData.standardDeliveryDays.max}
                value={formData.expressDeliveryDays.max}
                onChange={handleChange}
                required
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="page-content">
    <div className="w-full px-3 mx-auto xl:max-w-[85%]">
    <Card className="shadow-md">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-2xl font-bold">Carrier Performance</CardTitle>
          <CardDescription>Manage carrier delivery performance metrics</CardDescription>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="mr-2 h-4 w-4" /> Add New
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            <span className="ml-2 text-lg">Loading...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
              <div className="table-responsive">
            <Table className="min-w-full">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Carrier Name</TableHead>
                  <TableHead className="w-[180px]">State</TableHead>
                  <TableHead className="w-[180px] text-center">Standard Delivery (Days)</TableHead>
                  <TableHead className="w-[180px] text-center">Express Delivery (Days)</TableHead>
                  <TableHead className="w-[100px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {carriers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      No carrier performance data found
                    </TableCell>
                  </TableRow>
                ) : (
                  carriers?.map((carrier) => (
                    <TableRow key={carrier._id}>
                      <TableCell className="font-medium w-[100px]">{carrier.carrierName}</TableCell>
                      <TableCell className="w-[100px]">{carrier.state}</TableCell>
                      <TableCell className="w-[180px] text-center">{`${carrier.standardDeliveryDays.min} - ${carrier.standardDeliveryDays.max}`}</TableCell>
                      <TableCell className="w-[180px] text-center">{`${carrier.expressDeliveryDays.min} - ${carrier.expressDeliveryDays.max}`}</TableCell>
                      <TableCell className="w-[100px] text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <span className="sr-only">Open menu</span>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openViewDialog(carrier)}>
                              <Eye className="mr-2 h-4 w-4" /> View
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEditDialog(carrier)}>
                              <Edit className="mr-2 h-4 w-4" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => openDeleteDialog(carrier)}
                            >
                              <Trash className="mr-2 h-4 w-4" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          </div>
        )}
      </CardContent>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Create Carrier Performance</DialogTitle>
            <DialogDescription>
              Add a new carrier delivery performance record
            </DialogDescription>
          </DialogHeader>
          {renderForm(false)}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              onClick={handleCreate}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Carrier Performance</DialogTitle>
            <DialogDescription>
              Update carrier delivery performance metrics
            </DialogDescription>
          </DialogHeader>
          {renderForm(true)}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              onClick={handleUpdate}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Carrier Performance Details</DialogTitle>
            <DialogDescription>
              View carrier delivery performance metrics
            </DialogDescription>
          </DialogHeader>
          {selectedCarrier && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Carrier Name</h3>
                  <p className="mt-1 text-lg font-semibold">{selectedCarrier.carrierName}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">State</h3>
                  <p className="mt-1 text-lg font-semibold">{selectedCarrier.state}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 border rounded-lg bg-gray-50">
                  <h3 className="text-sm font-medium text-gray-500">Standard Delivery Days</h3>
                  <div className="mt-2 flex items-center justify-center">
                    <div className="text-center px-4">
                      <p className="text-sm text-gray-500">Min</p>
                      <p className="text-2xl font-bold">{selectedCarrier.standardDeliveryDays.min}</p>
                    </div>
                    <div className="text-xl font-light text-gray-400">-</div>
                    <div className="text-center px-4">
                      <p className="text-sm text-gray-500">Max</p>
                      <p className="text-2xl font-bold">{selectedCarrier.standardDeliveryDays.max}</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 border rounded-lg bg-gray-50">
                  <h3 className="text-sm font-medium text-gray-500">Express Delivery Days</h3>
                  <div className="mt-2 flex items-center justify-center">
                    <div className="text-center px-4">
                      <p className="text-sm text-gray-500">Min</p>
                      <p className="text-2xl font-bold">{selectedCarrier.expressDeliveryDays.min}</p>
                    </div>
                    <div className="text-xl font-light text-gray-400">-</div>
                    <div className="text-center px-4">
                      <p className="text-sm text-gray-500">Max</p>
                      <p className="text-2xl font-bold">{selectedCarrier.expressDeliveryDays.max}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Created At</h3>
                  <p className="mt-1">{new Date(selectedCarrier.createdAt).toLocaleString()}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Updated At</h3>
                  <p className="mt-1">{new Date(selectedCarrier.updatedAt).toLocaleString()}</p>
                </div>
              </div>

              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => openEditDialog(selectedCarrier)}
                >
                  <Edit className="mr-2 h-4 w-4" /> Edit
                </Button>
                <DialogClose asChild>
                  <Button>Close</Button>
                </DialogClose>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete Carrier Performance</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this carrier performance record? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {selectedCarrier && (
            <div className="py-4">
              <p className="mb-2"><span className="font-semibold">Carrier Name:</span> {selectedCarrier.carrierName}</p>
              <p><span className="font-semibold">State:</span> {selectedCarrier.state}</p>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash className="mr-2 h-4 w-4" /> Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
    </div>
    <ToastContainer />
    </div>
  );
}

export default CarrierPerformanceList;
